import React from 'react';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { TrendingUp, Users, Target, Zap } from 'lucide-react';

export default function About({ user }) {
  return (
    <div className="min-h-screen">
      <Navigation user={user} />
      
      <main className="container mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-8">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-none mb-6" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="about-title">
              AI FIRST<br />TRANSFORMATION
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Transformando empresas pela Inteligência Artificial
            </p>
            <div className="space-y-4 text-base leading-relaxed">
              <p>
                A AIFT é uma consultoria especializada em transformação digital com foco em Inteligência Artificial. 
                Nossa missão é capacitar executivos, gestores e empresas a adotarem uma mentalidade AI-First, 
                colocando a IA no centro da estratégia de negócios.
              </p>
              <p>
                Não se trata apenas de usar ferramentas de IA, mas de incorporá-la ao DNA organizacional, 
                onde a IA é o ponto de partida para resolver problemas, não um adendo.
              </p>
            </div>
          </div>
          
          <div className="md:col-span-4">
            <div
              className="h-full min-h-[300px] bg-cover bg-center rounded-none"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1697059361461-b81d0e98c3af?crop=entropy&cs=srgb&fm=jpg&q=85')",
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <Card className="p-6 bg-primary text-primary-foreground rounded-none border-none" data-testid="stat-card-experience">
            <TrendingUp className="h-8 w-8 mb-4" />
            <div className="text-3xl font-bold mb-2">15+</div>
            <div className="text-sm uppercase tracking-wide">Anos de Experiência</div>
          </Card>

          <Card className="p-6 rounded-none border border-border" data-testid="stat-card-companies">
            <Users className="h-8 w-8 mb-4" strokeWidth={1.5} />
            <div className="text-3xl font-bold mb-2">400k+</div>
            <div className="text-sm uppercase tracking-wide text-muted-foreground">Usuários Impactados</div>
          </Card>

          <Card className="p-6 rounded-none border border-border" data-testid="stat-card-revenue">
            <Target className="h-8 w-8 mb-4" strokeWidth={1.5} />
            <div className="text-3xl font-bold mb-2">R$900M+</div>
            <div className="text-sm uppercase tracking-wide text-muted-foreground">Receita Gerada</div>
          </Card>

          <Card className="p-6 rounded-none border border-border" data-testid="stat-card-companies-served">
            <Zap className="h-8 w-8 mb-4" strokeWidth={1.5} />
            <div className="text-3xl font-bold mb-2">12+</div>
            <div className="text-sm uppercase tracking-wide text-muted-foreground">Empresas Líderes</div>
          </Card>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold tracking-tight mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>Nossa Proposta</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">01</div>
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Estratégia AI-First</h3>
              <p className="text-muted-foreground">
                Redesenho completo de processos e modelos de negócio adaptados para a era da IA. 
                Não é sobre ferramentas, é sobre arquitetura de valor.
              </p>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">02</div>
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Growth & Revenue</h3>
              <p className="text-muted-foreground">
                Abordagem integrada que une estratégia, growth e IA. Otimização de canais e funis 
                de vendas com dados preditivos e performance escalável.
              </p>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">03</div>
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Lean Operation</h3>
              <p className="text-muted-foreground">
                Implementação de processos inovadores e agentes de AI aumentando a escala 
                mantendo ou reduzindo os custos operacionais.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-16">
          <h2 className="text-3xl font-bold tracking-tight mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>Nossos Pilares</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Regra 10-20-70</h3>
              <p className="text-muted-foreground">
                O sucesso da transformação depende 10% de algoritmos, 20% de tecnologia e 70% de pessoas, processos e cultura.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>FOMO vs. FOBO</h3>
              <p className="text-muted-foreground">
                O dilema da liderança entre o medo de ficar para trás (Fear of Missing Out) e o medo de errar 
                (Fear of Better Options). Como superar a paralisia decisória.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Framework DRI</h3>
              <p className="text-muted-foreground">
                Deploy (Ganhos imediatos/produtividade), Reshape (Redesenho de processos críticos) e 
                Invent (Novos modelos de negócio).
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}